import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import { ref, createRef } from 'lit/directives/ref.js';
import throttle from 'lodash/throttle';
import { isLaptopMaxWidth } from '@/helpers';
import styles from './dropdown.scss?inline';
import { CONTENT_CHANGE_EVENT } from '@/components/modals/constants.js';

export default class SotbitDropdown extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        open: { type: Boolean, reflect: true },
        mobileModal: { type: String },
        mobileModalSlot: { type: String },
        value: { type: String },
        isSelect: { type: Boolean, attribute: 'is-select' },
        maxHeight: { type: String, attribute: 'max-height' },
        itemsTop: { type: Boolean, attribute: 'items-top' },
        itemsBottom: { type: Boolean, attribute: 'items-bottom' },
        itemsRight: { type: Boolean, attribute: 'items-right' },
        variant: { type: String },
        useTeleport: { type: Boolean, attribute: 'use-teleport' },
        teleportTo: { type: String, attribute: 'teleport-to' },
        toggleContentVariant: { type: String, attribute: 'toggle-content-variant' },
        hideOnSelect: { type: String, attribute: 'hide-on-select' },
        showOnHover: { type: Boolean, attribute: 'show-on-hover' },
        disableAutoHide: { type: Boolean, attribute: 'disable-auto-hide' },
        disabled: { type: Boolean, reflect: true },
    };

    #HOVER_TIMEOUT = 100;

    constructor() {
        super();

        this.open = false;
        this.value = null;
        this.variant = null;
        this.isSelect = false;
        this.itemsTop = false;
        this.itemsBottom = false;
        this.itemsRight = false;
        this.useTeleport = false;
        this.teleportTo = null;
        this.toggleContentVariant = 'on';
        this.hideOnSelect = '';
        this._dropdownItems = [];
        this._modalTag = 'sotbit-modal';
        this._modalSlot = 'content';
        this._modalOpenAttr = 'open';
        this._modal = null;
        this.disableAutoHide = false;
        this._dropdownContentRef = createRef();
        this._footerContentRef = createRef();
        this._hoverTimeout = null;
        this._isLaptop = false;
        this.portalNode = null;
        this._relativeElement = null;
        this.isDefaultValueSet = false;
    }

    getRelativeElement() {
        return this._relativeElement;
    }

    setRelativeElement(relativeElement) {
        this._relativeElement = relativeElement;
    }

    connectedCallback() {
        super.connectedCallback();

        window.addEventListener('resize', this._windowResizeHandler, {
            passive: true,
        });
        this._isLaptop = isLaptopMaxWidth();

        window.addEventListener('DOMContentLoaded', this._handleDOMContentLoaded);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this.removeEventListener('mouseenter', this._handleMouseEnter);
        this.removeEventListener('mouseleave', this._handleMouseLeave);
        window.removeEventListener('resize', this._windowResizeHandler);

        window.removeEventListener('DOMContentLoaded', this._handleDOMContentLoaded);
    }

    getItems() {
        return this._dropdownItems;
    }

    getModal() {
        return this._modal;
    }

    _windowResizeHandler = throttle(async () => {
        if (!this.open) {
            return;
        }

        if (!this.disableAutoHide) {
            this.hide();
            return;
        }

        const isLaptop = isLaptopMaxWidth();
        if (this._isLaptop !== isLaptop && this.toggleContentVariant !== 'off') {
            await this.hide();
            this.show();
            this._isLaptop = isLaptop;
        }
    }, 200);

    _handleDOMContentLoaded = () => {
        this.addEventListener('mouseenter', this._handleMouseEnter);
        this.addEventListener('mouseleave', this._handleMouseLeave);
    };

    _handleMouseEnter = async () => {
        if (!this.showOnHover || this._isShowLaptopModal()) {
            return;
        }

        clearTimeout(this._hoverTimeout);

        await this.hide();
        this.show();

        this._modal.addEventListener(
            'mouseenter',
            () => {
                clearTimeout(this._hoverTimeout);

                this._modal.addEventListener('mouseleave', () => {
                    this._hoverTimeout = setTimeout(() => {
                        this.hide();
                    }, this.#HOVER_TIMEOUT);
                });
            },
            { once: true }
        );
    };

    _handleMouseLeave = () => {
        if (!this.showOnHover || this._isShowLaptopModal()) {
            return;
        }

        this._hoverTimeout = setTimeout(() => {
            this.hide();
        }, this.#HOVER_TIMEOUT);
    };

    _handleAnchorClick({ target }) {
        if (this.disabled || target.disabled) {
            return;
        }

        if (this.showOnHover && !this._isShowLaptopModal()) {
            return;
        }

        if (this.open) {
            this.hide();
        } else {
            this.show();
        }
    }

    _isShowLaptopModal() {
        return this.toggleContentVariant !== 'off' && isLaptopMaxWidth();
    }

    _getModal() {
        let modalClassName = null;
        if (this.hasAttribute('modal-class')) {
            modalClassName = this.getAttribute('modal-class');
        }

        if (this._isShowLaptopModal()) {
            const modal = document.createElement(this._modalTag);
            modal.setAttribute('hide-by-overlay', '');
            let variant = this.getAttribute('variant')?.trim();

            if (variant && !variant?.startsWith('dropdown')) {
                variant = `dropdown-${variant}`;
            }

            if (variant?.length > 0) {
                modal.setAttribute('variant', `${variant}`);
            } else if (this.isSelect) {
                modal.setAttribute('variant', 'dropdown-select');
            } else {
                modal.setAttribute('variant', 'dropdown');
            }

            if (modalClassName !== null) {
                modal.className = modalClassName;
            }

            return modal;
        }

        if (this.useTeleport) {
            const modal = document.createElement('sotbit-dropdown-content');
            modal.setDropdown(this);
            modal.setAttribute('parent-id', this.id);
            modal.toggleAttribute('items-top', this.itemsTop);
            modal.toggleAttribute('items-bottom', this.itemsBottom);
            modal.toggleAttribute('items-right', this.itemsRight);
            modal.setAttribute('variant', this.variant);
            modal.setAttribute('use-teleport', this.useTeleport);
            modal.setRelativeElement(this._relativeElement);

            if (this.maxHeight) {
                modal.style.setProperty('--dropdown-max-height', this.maxHeight);
            }

            if (modalClassName !== null) {
                modal.className = modalClassName;
            }

            return modal;
        }

        return null;
    }

    show() {
        this._ignoreOpenAttrChanged = true;
        this.open = true;

        this._dropdownItems?.forEach((dropdownItem) => {
            dropdownItem?.removeEventListener('click', this._handleSelect);
            dropdownItem?.addEventListener('click', this._handleSelect);
        });

        this._modal = this._getModal();

        if (this._modal === null) {
            document.addEventListener('click', this._handleDocumentClick);
            return;
        }

        this._dropdownItems.forEach((item) => {
            this._modal.appendChild(item);
        });

        if (this._footerContentRef.value) {
            this._modal.appendChild(this._footerContentRef.value);
        }

        if (this._isShowLaptopModal()) {
            this._dropdownItems.forEach((item) => {
                item.setAttribute('slot', this._modalSlot);
            });

            this._footerContentRef.value?.setAttribute('slot', 'footer');

            this._modal.addEventListener(
                'close',
                () => {
                    this.removeAttribute('open');
                },
                { once: true }
            );

            this._modal.addEventListener(
                'remove',
                () => {
                    this.removeAttribute('open');
                },
                { once: true }
            );

            this._modal.addEventListener(CONTENT_CHANGE_EVENT, (event) => {
                if (!this.open) {
                    return;
                }

                this._dropdownItems = event.detail;
            });

            this._createPortalNode();
            this.portalNode.moveTo('body');

            setTimeout(() => {
                this._modal.setAttribute(this._modalOpenAttr, '');
            });
        } else {
            if (this.useTeleport) {
                this._createPortalNode();
                const teleportTo = !isLaptopMaxWidth() && this.teleportTo ? this.teleportTo : 'body';
                this.portalNode.moveTo(teleportTo);
            }
        }

        setTimeout(() => {
            document.addEventListener('click', this._handleDocumentClick);
        });

        this.dispatchEvent(new Event('open'));
    }

    _createPortalNode() {
        this.portalNode = document.createElement('sotbit-portal');
        this.portalNode.appendChild(this._modal);
    }

    async hide() {
        this._ignoreOpenAttrChanged = true;
        this.open = false;

        if (this._modal !== null) {
            this._dropdownItems.forEach((dropdownItem) => {
                dropdownItem.removeAttribute('slot');
                this.appendChild(dropdownItem);
            });

            if (this._footerContentRef.value) {
                this._footerContentRef.value.setAttribute('slot', 'footer');
                this.appendChild(this._footerContentRef.value);
            }

            if (typeof this._modal?.hide !== 'undefined') {
                await this._modal?.hide();
            }

            this._modal?.remove();
            this._modal = null;
        }

        this.portalNode?.remove();
        this.portalNode = null;

        document.removeEventListener('click', this._handleDocumentClick);
    }

    getItemsContainer() {
        return this._modal ?? this;
    }

    updated(_changedProperties) {
        super.updated(_changedProperties);

        if (this.open && !this.useTeleport) {
            this._dropdownContentRef?.value?.setDropdown(this);
        }

        if (_changedProperties.has(this._modalOpenAttr) && this.anchorElements && this.anchorElements.length) {
            this.anchorElements.forEach((el) => {
                if (_changedProperties.get(this._modalOpenAttr)) {
                    el.removeAttribute(this._modalOpenAttr);
                } else {
                    el.setAttribute(this._modalOpenAttr, '');
                }
            });
        }

        if (
            _changedProperties.has(this._modalOpenAttr) &&
            typeof _changedProperties.get(this._modalOpenAttr) !== 'undefined'
        ) {
            if (this._ignoreOpenAttrChanged) {
                this._ignoreOpenAttrChanged = false;
            } else {
                if (this.open) {
                    this.show();
                } else {
                    this.hide();
                }
            }
        }
    }

    _handleDocumentClick = (e) => {
        if (this.disableAutoHide) {
            return;
        }

        const composedPath = e.composedPath();

        if (!composedPath.includes(this._modal) && !composedPath.includes(this)) {
            this.hide();
        }
    };

    handleSelect(target) {
        if (target.active) {
            return;
        }

        for (const child of [...this.children, ...this._dropdownItems]) {
            if (child.getAttribute('slot') === 'anchor') {
                const anchorContent = child.querySelector('[slot="content"]');

                if (anchorContent) {
                    anchorContent.innerText = target.getAttribute('display-value') ?? target?.getAttribute('value');
                    continue;
                }
            }

            child?.removeAttribute('active');
        }

        target.setAttribute('active', '');
        this.value = target.getAttribute('value');

        this.dispatchEvent(
            new CustomEvent('change', {
                detail: { value: this.value, target },
                bubbles: true,
                composed: true,
            })
        );
    }

    _handleSelect = (e) => {
        if (this.hideOnSelect !== 'false') {
            this.hide();
        }

        if (!this.isSelect) {
            return;
        }

        if (e.target.hasAttribute('value')) {
            this.handleSelect(e.target);
        } else if (e.target.closest('[value]')) {
            this.handleSelect(e.target.closest('[value]'));
        }
    };

    handleSlotChange({ currentTarget }) {
        if (this.open) {
            return;
        }

        this._dropdownItems = currentTarget.assignedElements();

        if (!this.isSelect || this.isDefaultValueSet) {
            return;
        }

        this._dropdownItems.forEach((el) => {
            if (el.hasAttribute('default')) {
                this.handleSelect(el);
                this.isDefaultValueSet = true;
            }
        });
    }

    handleSlotchangeAnchor(e) {
        const childNodes = e.target.assignedNodes({ flatten: true });
        this.anchorElements = childNodes;
    }

    handleFooterSlotChange({ currentTarget }) {
        if (this.open) {
            return;
        }

        this._footerContentRef.value = currentTarget.assignedElements()[0];
    }

    render() {
        return html`
            <div class="dropdown-anchor" part="dropdown-anchor" @click="${this._handleAnchorClick}">
                <slot name="anchor" @slotchange=${this.handleSlotchangeAnchor}></slot>
            </div>
            ${this.useTeleport
                ? html` <div hidden>
                      <slot @slotchange=${this.handleSlotChange}></slot>
                      <slot @slotchange=${this.handleFooterSlotChange} name="footer"></slot>
                  </div>`
                : this.open && !this._isShowLaptopModal()
                  ? html`
                        <sotbit-dropdown-content
                            parent-id="${this.id || nothing}"
                            disable-auto-hide="${this.disableAutoHide || nothing}"
                            part="dropdown-content"
                            exportparts="content"
                            ${ref(this._dropdownContentRef)}
                            ?items-top="${this.itemsTop}"
                            ?items-bottom="${this.itemsBottom}"
                            ?items-right="${this.itemsRight}"
                            ?max-height="${this.maxHeight}"
                            variant="${this.variant || nothing}"
                        >
                            <slot @slotchange=${this.handleSlotChange}></slot>
                            <slot @slotchange=${this.handleFooterSlotChange} name="footer"></slot>
                        </sotbit-dropdown-content>
                    `
                  : html` <div hidden>
                        <slot @slotchange=${this.handleSlotChange}></slot>
                        <slot @slotchange=${this.handleFooterSlotChange} name="footer"></slot>
                    </div>`}
        `;
    }
}
